import {
  CallTransferStatus,
  InteractionContactType,
  WarmTransferDestinationType,
} from '@api/generated/graphql';
import { getWarmTransferDestination } from './hooks/getWarmTransferDestination';
import { getWorkflowTransferStatus } from './hooks/getWorkflowTransferStatus';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import getTransferStatus from '@api/queries/getTransferStatus';
import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import { authState } from '@modules/AuthModule/state';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
  OperationNames,
  CallDirection,
  CallConnectionErrorTypes,
} from '@modules/DiagnosticsModule';
import InteractionsModule from '@modules/InteractionsModule';
import { serviceState } from '@modules/ServiceModule/workspace/lib/state';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import {
  CallEvent,
  PcsConversationFetchedEvent,
} from '@modules/SoftPhoneModule/types';
import TasksModule from '@modules/TasksModule';
import TechniciansModule, {
  TechnicianTableRow,
} from '@modules/TechniciansModule';
import ToastModule from '@modules/ToastModule';
import {
  ColdCallTransferDetails,
  WarmTransferContext,
  WarmTransferTaskAttributes,
  EmployeeTransferStatus,
  QueueTransferStatus,
  TechnicianTransferStatus,
} from './types';
import { OutboundCallEntity } from '@lib/types/common-call-types';

/**
 * Checks if a remote identity matches a target phone number.
 * Handles formatting variations (e.g. "Device QuickCall (+1234567890)") by checking both directions.
 */
function matchesTargetPhone(
  targetPhone: string,
  remoteIdentity: string,
): boolean {
  return (
    targetPhone.includes(remoteIdentity) || remoteIdentity.includes(targetPhone)
  );
}

/**
 * TransfersModule — manages cold and warm call transfers.
 *
 * Transfer Lifecycle:
 *
 *   Cold transfer:
 *     makeColdCallTransfer → handleInProgressTransfer (polls every 10s) → success/failure toast
 *     Paths: employee (agent polling) | queue (workflow polling) | technician (no polling) | external (no polling)
 *
 *   Warm transfer:
 *     startWarmCallTransfer → makeOutboundCall (warm-up call to target)
 *       → phone.call.placed  → updates warmTransferContext with warmUpCallId
 *       → phone.call.answered → marks isWarmUpCallConnected = true
 *       → user clicks "Complete Transfer"
 *     completeWarmCallTransfer → resolves destination → ends warm-up call → makeColdCallTransfer
 *       For queue transfers: queries GetWarmTransferDestination to resolve final destination
 *       Sets originalTransferType so polling uses the correct query (workflow vs employee)
 *
 *   Cleanup:
 *     phone.call.ended/failed/missed → deleteWarmTransferContexts (removes context from both tasks)
 *
 *   On reload:
 *     user.status.initialized → handleInProgressTransfersOnLoad (resumes polling for in-progress transfers)
 *
 * State is tracked via task attributes:
 *   - transferStatus: EmployeeTransferStatus | QueueTransferStatus | TechnicianTransferStatus
 *   - warmTransferContext: WarmTransferContext (synced across original + warm-up call tasks)
 */
export class TransfersModule extends AppModule {
  /** Registers event listeners for call lifecycle and transfer recovery on reload. */
  override preload(): void {
    this.app.events.on('user.status.initialized', () => {
      // process any in-progress transfer tasks when the user status is initialized
      this.handleInProgressTransfersOnLoad();
    });

    this.app.events.on('phone.call.placed', (ev: CallEvent) => {
      this.handlePhoneCallPlaced(ev);
    });

    this.app.events.on('phone.call.answered', (ev: CallEvent) => {
      this.handlePhoneCallAnswered(ev);
    });

    this.app.events.on('phone.call.ended', (ev: CallEvent) => {
      this.handlePhoneCallEnded(ev);
    });

    this.app.events.on('phone.call.failed', (ev: CallEvent) => {
      this.handlePhoneCallEnded(ev);
    });

    this.app.events.on('phone.call.missed', (ev: CallEvent) => {
      this.handlePhoneCallEnded(ev);
    });

    this.app.events.on<PcsConversationFetchedEvent>(
      'phone.pcs.conversation.fetched',
      (ev) => {
        this.handlePcsConversationFetched(ev);
      },
    );
  }

  /**
   * Initiates a cold call transfer. Routes to one of four paths based on destination:
   * employee (poll via getTransferStatus), queue (poll via getWorkflowTransferStatus),
   * technician (no poll, agent closes), or external (no poll, immediate toast).
   * When originalTransferType is Queue, forces workflow polling regardless of resolved transferType.
   */
  public async makeColdCallTransfer({
    taskOrCallId,
    transferToNumber,
    agentId,
    transferType,
    originalTransferType,
    queueId,
    queueName,
    tenantId,
    technicianId,
    technicianName,
    fromWarmTransfer,
  }: ColdCallTransferDetails): Promise<void> {
    this.app.logger.info('transfer:cold:initiated', {
      taskOrCallId,
      transferType,
      originalTransferType,
      transferToNumber,
      agentId,
      fromWarmTransfer: !!fromWarmTransfer,
    });

    const agentContactCenterId = this.getAgentContactCenterId(
      agentId,
      transferToNumber,
    );

    const task =
      TasksModule.getTask(taskOrCallId) ??
      TasksModule.getTaskByCallId(taskOrCallId);

    if (!task) {
      this.app.logger.error('transfer:cold:task-not-found', { taskOrCallId });
      return;
    }

    // Check if this is a queue transfer (when a feature flag is enabled)
    const isTransferringToQueue =
      FeatureFlagModule.isEnabled(
        FeatureFlagKeys.featureInboxCallTransferToQueue,
      ) && transferType === OutboundCallEntity.Queue;

    const isQueueOriginated = originalTransferType === OutboundCallEntity.Queue;

    if (agentContactCenterId) {
      this.app.logger.debug('transfer:cold:path', {
        path: 'employee',
        taskId: task.id,
      });
      // Internal agent transfer: setup status monitoring for transfer lifecycle
      const transferStatus: EmployeeTransferStatus = {
        inProgress: true,
        agentId: agentContactCenterId,
        transferType: OutboundCallEntity.Employees,
        originalTransferType,
        transferInitiatorAgentId: authState.user?.contactCenterUserId ?? '',
        initiatedAt: new Date(),
      };
      TasksModule.setTaskAttribute(task.id, 'transferStatus', transferStatus);
      this.handleInProgressTransfer(task.id);
    } else if (isTransferringToQueue || isQueueOriginated) {
      this.app.logger.debug('transfer:cold:path', {
        path: 'queue',
        taskId: task.id,
        isQueueOriginated,
      });
      // Queue transfer (direct or resolved from warm transfer): setup status monitoring via workflow polling
      const transferStatus: QueueTransferStatus = {
        inProgress: true,
        transferType: OutboundCallEntity.Queue,
        originalTransferType,
        transferInitiatorAgentId: authState.user?.contactCenterUserId ?? '',
        queueId,
        queueName,
        initiatedAt: new Date(),
      };
      TasksModule.setTaskAttribute(task.id, 'transferStatus', transferStatus);

      this.handleInProgressTransfer(task.id);
    } else if (transferType === OutboundCallEntity.Technicians) {
      this.app.logger.debug('transfer:cold:path', {
        path: 'technician',
        taskId: task.id,
      });
      // Technician transfer: no status polling — agent always close & classifies
      const transferStatus: TechnicianTransferStatus = {
        transferType: OutboundCallEntity.Technicians,
        tenantId,
        initiatedAt: new Date(),
      };
      TasksModule.setTaskAttribute(task.id, 'transferStatus', transferStatus);
      ToastModule.showToast({
        title: 'Transfer completed.',
        status: 'success',
        duration: 5000,
      });
    } else {
      this.app.logger.debug('transfer:cold:path', {
        path: 'external',
        taskId: task.id,
      });
      // External transfer: show notification and proceed with standard transfer
      ToastModule.showToast({
        title: 'Transfer completed.',
        status: 'success',
        duration: 5000,
      });
    }

    // Attach technician contact to the original customer task before the SIP
    // transfer disposes it; fire-and-forget so the transfer flow is never
    // blocked.
    this.attachTechnicianContact({
      taskId: task.id,
      transferType,
      transferToNumber,
      agentContactCenterId,
      technicianId,
      technicianName,
    }).catch((e) => this.app.logger.error('attachTechnicianContact failed', e));

    // transfer call
    SoftPhoneModule.transferNumber(transferToNumber);

    this.app.logger.info('transfer:cold:completed', {
      taskId: task.id,
      transferType,
      fromWarmTransfer: !!fromWarmTransfer,
    });
  }

  /**
   * Writes the technician contact to the original customer task. Tech tab uses
   * the provided id; keypad/external paths look up the dialed phone via
   * TechniciansModule. Employee/queue paths are skipped. Gated by
   * featureDisplayTechnicianConversationHistory.
   */
  private async attachTechnicianContact({
    taskId,
    transferType,
    transferToNumber,
    agentContactCenterId,
    technicianId,
    technicianName,
  }: {
    taskId: string;
    transferType: OutboundCallEntity;
    transferToNumber: string;
    agentContactCenterId: string | null;
    technicianId: string | undefined;
    technicianName: string | undefined;
  }): Promise<void> {
    if (
      !FeatureFlagModule.isEnabled(
        FeatureFlagKeys.featureDisplayTechnicianConversationHistory,
      )
    ) {
      return;
    }

    if (transferType === OutboundCallEntity.Technicians) {
      if (!technicianId) return;
      this.writeTechnicianContact(taskId, {
        id: technicianId,
        name: technicianName ?? '',
      });
      return;
    }

    // Keypad / external: only look up when the destination isn't a resolved agent.
    if (transferType !== OutboundCallEntity.Keypad || agentContactCenterId) {
      return;
    }

    const technician =
      await TechniciansModule.findTechnicianByPhone(transferToNumber);
    if (!technician) return;

    this.writeTechnicianContact(taskId, technician);
  }

  private writeTechnicianContact(
    taskId: string,
    technician: Pick<TechnicianTableRow, 'id' | 'name'>,
  ): void {
    TasksModule.setContactsOnTask(taskId, [
      {
        type: InteractionContactType.Technician,
        id: technician.id,
        name: technician.name,
      },
    ]);
  }

  /**
   * Starts a warm transfer by creating a warm-up call to the target.
   * Sets up WarmTransferContext on the original task, then dials the target.
   * Transfer completes only when the user clicks "Complete Transfer" ({@link completeWarmCallTransfer}).
   */
  public async startWarmCallTransfer(
    taskId: string,
    transferToNumber: string,
    transferType: OutboundCallEntity,
    agentId?: string,
    displayName?: string,
    technicianId?: string,
    technicianName?: string,
  ): Promise<void> {
    this.app.logger.info('transfer:warm:initiated', {
      taskId,
      transferToNumber,
      transferType,
      agentId,
    });

    // put the main call on hold
    const originalTask = TasksModule.getTask(taskId);
    if (!originalTask) {
      this.app.logger.error('transfer:warm:task-not-found', { taskId });
      return;
    }

    // determine if transferring to an agent in any way via table or a keypad
    const agentContactCenterId = this.getAgentContactCenterId(
      agentId,
      transferToNumber,
    );
    const agentName = this.getAgentName(agentContactCenterId);

    // create a new warmTransferContext object to be added into two tasks
    const warmTransferContext = {
      isWarmUpCallConnected: false,
      originalCallId: originalTask.attributes?.callId ?? '',
      warmUpCallId: null,
      targetAgentId: agentContactCenterId,
      targetPhoneNumber: transferToNumber,
      transferType,
      initiatedAt: new Date(),
      technicianId,
      technicianName,
    } as WarmTransferContext;

    TasksModule.setTaskAttribute(
      originalTask.id,
      'warmTransferContext',
      warmTransferContext,
    );

    const targetName = displayName ?? agentName;

    // create a new outbound task for the warm transfer with context attached in attributes
    await this.makeOutboundCall(
      transferToNumber,
      warmTransferContext,
      targetName,
    );
  }

  /**
   * Completes a warm transfer: ends the warm-up call and initiates a cold transfer.
   * Non-queue transfers use the known destination directly.
   * Queue transfers query GetWarmTransferDestination to resolve the final target,
   * then pass originalTransferType so downstream polling uses getWorkflowTransferStatus.
   */
  public async completeWarmCallTransfer(
    warmTransferContext: WarmTransferContext,
  ): Promise<void> {
    this.app.logger.info('transfer:warm:completing', {
      transferType: warmTransferContext.transferType,
      originalCallId: warmTransferContext.originalCallId,
      warmUpCallId: warmTransferContext.warmUpCallId,
    });

    // For transfers other than queue, we already know the destination — skip the query
    if (warmTransferContext.transferType !== OutboundCallEntity.Queue) {
      this.app.logger.info('transfer:warm:completing-direct', {
        originalCallId: warmTransferContext.originalCallId,
        transferType: warmTransferContext.transferType,
      });
      if (warmTransferContext.warmUpCallId) {
        SoftPhoneModule.endCall(warmTransferContext.warmUpCallId);
      } else {
        this.app.logger.warn('transfer:warm:complete-missing-warmup-call-id', {
          originalCallId: warmTransferContext.originalCallId,
        });
      }

      this.makeColdCallTransfer({
        taskOrCallId: warmTransferContext.originalCallId,
        transferToNumber: warmTransferContext.targetPhoneNumber,
        agentId: warmTransferContext.targetAgentId ?? undefined,
        transferType: warmTransferContext.transferType,
        technicianId: warmTransferContext.technicianId,
        technicianName: warmTransferContext.technicianName,
        fromWarmTransfer: true,
      });
      return;
    }

    // For queue transfers, resolve the destination via query
    const warmUpCallTask = warmTransferContext.warmUpCallId
      ? TasksModule.getTaskByCallId(warmTransferContext.warmUpCallId)
      : undefined;
    const warmUpConversationSid = warmUpCallTask?.id;

    if (!warmUpConversationSid) {
      this.app.logger.error('transfer:warm:warmup-call-not-found', {
        originalCallId: warmTransferContext.originalCallId,
        warmUpCallId: warmTransferContext.warmUpCallId,
      });
      ToastModule.showToast({
        title:
          'Unable to complete transfer due to warm-up call not found. Please try to start the transfer again.',
        status: 'critical',
        duration: 5000,
      });
      return;
    }

    let destination: string | null;
    let destinationType: WarmTransferDestinationType | undefined;

    try {
      const result = await getWarmTransferDestination(warmUpConversationSid);
      destination = result.destination?.destination ?? null;
      destinationType = result.destinationType ?? undefined;
    } catch (error) {
      this.app.logger.error('transfer:warm:destination-query-failed', {
        warmUpConversationSid,
        originalCallId: warmTransferContext.originalCallId,
        error,
      });
      ToastModule.showToast({
        title:
          'Unable to complete warm transfer to a queue. Destination not found. Please try to start the transfer again.',
        status: 'critical',
        duration: 5000,
      });
      return;
    }

    if (!destination) {
      this.app.logger.error('transfer:warm:destination-not-found', {
        warmUpConversationSid,
        originalCallId: warmTransferContext.originalCallId,
      });
      ToastModule.showToast({
        title:
          'Unable to complete warm transfer to a queue. Destination not found. Please try to start the transfer again.',
        status: 'critical',
        duration: 5000,
      });
      return;
    }

    this.app.logger.info('transfer:warm:destination-resolved', {
      warmUpConversationSid,
      destination,
      destinationType,
    });

    // if the destination is an agent, we can handle this as an employee transfer
    this.app.logger.info('transfer:warm:completing-via-query', {
      originalCallId: warmTransferContext.originalCallId,
      destinationType,
      destination,
    });

    this.completeQueueWarmTransfer(
      warmTransferContext,
      destination,
      destinationType,
    );
  }

  /** Routes a resolved queue warm transfer to the correct cold-transfer path (user or external). */
  private completeQueueWarmTransfer(
    warmTransferContext: WarmTransferContext,
    destination: string,
    destinationType: WarmTransferDestinationType | undefined,
  ): void {
    if (destinationType === WarmTransferDestinationType.User) {
      const currentAgentId = authState.user?.contactCenterUserId;
      const isSelfTransfer = !!currentAgentId && destination === currentAgentId;

      if (isSelfTransfer) {
        this.handleSelfTransfer(
          warmTransferContext,
          destination,
          currentAgentId,
        );
        return;
      }

      const userPhoneNumber =
        serviceState.userByContactCenterUserId[destination]?.personalLine
          ?.phoneNumber;

      if (!userPhoneNumber) {
        this.app.logger.error('transfer:warm:agent-phone-not-found', {
          destination,
          originalCallId: warmTransferContext.originalCallId,
        });
        ToastModule.showToast({
          title:
            'Unable to complete transfer. Agent phone number not found. Please try again.',
          status: 'critical',
          duration: 5000,
        });
        return;
      }

      if (warmTransferContext.warmUpCallId) {
        SoftPhoneModule.endCall(warmTransferContext.warmUpCallId);
      } else {
        this.app.logger.warn('transfer:warm:complete-missing-warmup-call-id', {
          originalCallId: warmTransferContext.originalCallId,
        });
      }

      this.makeColdCallTransfer({
        taskOrCallId: warmTransferContext.originalCallId,
        transferToNumber: userPhoneNumber,
        agentId: destination,
        transferType: OutboundCallEntity.Employees,
        originalTransferType: warmTransferContext.transferType,
        fromWarmTransfer: true,
      });
    } else {
      // destination is external — end the warm-up call and transfer the original call
      if (warmTransferContext.warmUpCallId) {
        SoftPhoneModule.endCall(warmTransferContext.warmUpCallId);
      } else {
        this.app.logger.warn('transfer:warm:complete-missing-warmup-call-id', {
          originalCallId: warmTransferContext.originalCallId,
        });
      }

      this.makeColdCallTransfer({
        taskOrCallId: warmTransferContext.originalCallId,
        transferToNumber: destination,
        transferType: OutboundCallEntity.Keypad,
        originalTransferType: warmTransferContext.transferType,
        fromWarmTransfer: true,
      });
    }
  }

  /**
   * Once a warm-up call is placed, marks its interaction as inactive via the BFF
   * so it isn't counted as a primary interaction. On any failure — including
   * "Interaction not found" races while the BFF catches up with the newly created
   * warm-up call and BFF outages — we retry up to 20 times with a 5s delay
   * between attempts (~100s coverage window). The final failure is logged
   * silently so it doesn't surface to the agent or break the transfer flow.
   */
  private async deactivateWarmUpInteraction(
    channelId: string,
    warmUpCallId: string,
    attempt = 1,
  ): Promise<void> {
    const MAX_ATTEMPTS = 20;
    const RETRY_DELAY_MS = 5000;

    try {
      await InteractionsModule.deactivateInteraction(channelId);
    } catch (error) {
      if (attempt < MAX_ATTEMPTS) {
        setTimeout(() => {
          void this.deactivateWarmUpInteraction(
            channelId,
            warmUpCallId,
            attempt + 1,
          );
        }, RETRY_DELAY_MS);
        return;
      }

      this.app.logger.error('transfer:warm:deactivate:failed', {
        warmUpCallId,
        channelId,
        attempt,
        error,
      });
    }
  }

  /** Places the warm-up outbound call via SoftPhone. On failure, reports the error and cleans up warm transfer context. */
  private async makeOutboundCall(
    phoneNumber: string,
    warmTransferContext: WarmTransferContext,
    displayName?: string,
  ): Promise<void> {
    const fixOutboundCallerIdErrors = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.fixOutboundCallerIdErrors,
    );

    try {
      if (!phoneNumber) {
        this.app.logger.error('transfer:warm:invalid-phone-number');
        throw new Error('Phone number is invalid');
      }

      const taskAttributes = {
        warmTransferContext,
      };

      const agentPhoneNumber = this.getAgentPhoneNumber() ?? '';
      const agentName = fixOutboundCallerIdErrors
        ? ''
        : this.getCurrentAgentName() ?? '';

      await this.makeOutboundViaSoftPhone(
        phoneNumber,
        agentPhoneNumber,
        agentName,
        taskAttributes,
        displayName,
      );

      this.app.logger.info('transfer:warm:outbound-call-placed', {
        phoneNumber,
        displayName,
      });
    } catch (error) {
      DiagnosticsModule.incrementCounter(MetricNames.ERROR_TOTAL, {
        [MetricTags.OPERATION_NAME]: OperationNames.CALL_CONNECTION,
        [MetricTags.DIRECTION]: CallDirection.Outbound.toLowerCase(),
        [MetricTags.ERROR_TYPE]: CallConnectionErrorTypes.WARM_TRANSFER,
      });
      const toastTitle =
        'We encountered an issue while making an outbound call. Please try again.';
      ToastModule.showToast({
        title: toastTitle,
        status: 'critical',
      });
      DiagnosticsModule.reportBackgroundError(
        error,
        'Failed to make an outbound call for a warm transfer.',
        {
          extra: {
            phoneNumber,
            warmTransferContext,
            displayName,
          },
        },
      );

      this.deleteWarmTransferContexts(warmTransferContext);
    }
  }

  /** Merges partial updates into the warm transfer context on both the original and warm-up call tasks. */
  private updateWarmTransferContexts(
    originalCallId: string,
    warmUpCallId: string,
    updates: Partial<WarmTransferContext>,
  ): void {
    const originalTask = TasksModule.getTaskByCallId(originalCallId);
    if (!originalTask) {
      this.app.logger.warn(
        'transfer:warm:context-update:original-task-not-found',
        {
          originalCallId,
          warmUpCallId,
        },
      );
      return;
    }

    const warmUpCallTask = TasksModule.getTaskByCallId(warmUpCallId);
    if (!warmUpCallTask) {
      this.app.logger.warn(
        'transfer:warm:context-update:warmup-task-not-found',
        {
          originalCallId,
          warmUpCallId,
        },
      );
      return;
    }

    const warmTransferContext: WarmTransferContext = {
      ...originalTask.attributes.warmTransferContext,
      ...updates,
    };

    // update the task with the new warm transfer context
    TasksModule.setTaskAttribute(
      originalTask.id,
      'warmTransferContext',
      warmTransferContext,
    );

    TasksModule.setTaskAttribute(
      warmUpCallTask.id,
      'warmTransferContext',
      warmTransferContext,
    );
  }

  /** Handles a self-transfer: marks the original task, shows a success toast, ends both calls, and cleans up context. */
  private handleSelfTransfer(
    warmTransferContext: WarmTransferContext,
    destination: string,
    currentAgentId: string,
  ): void {
    this.app.logger.info('transfer:warm:self-transfer-detected', {
      destination,
      currentAgentId,
      originalCallId: warmTransferContext.originalCallId,
    });

    const originalTask = TasksModule.getTaskByCallId(
      warmTransferContext.originalCallId,
    );
    if (originalTask) {
      TasksModule.setTaskAttribute(originalTask.id, 'isSelfTransfer', true);
      ToastModule.showToast({
        title: 'Transfer Completed',
        toastBody: 'Please close and classify the call.',
        status: 'success',
        duration: 5000,
      });
    } else {
      this.app.logger.error(
        'transfer:warm:self-transfer-original-task-not-found',
        {
          originalCallId: warmTransferContext.originalCallId,
          destination,
          currentAgentId,
        },
      );
    }

    if (warmTransferContext.warmUpCallId) {
      SoftPhoneModule.endCall(warmTransferContext.warmUpCallId);
    }
    SoftPhoneModule.endCall(warmTransferContext.originalCallId);
    this.deleteWarmTransferContexts(warmTransferContext);
  }

  /** Removes warm transfer context from both original and warm-up call tasks. Called on failure or call end. */
  private deleteWarmTransferContexts(
    warmTransferContext: WarmTransferContext | undefined | null,
  ): void {
    if (!warmTransferContext) {
      return;
    }

    this.app.logger.debug('transfer:warm:context-cleanup', {
      originalCallId: warmTransferContext.originalCallId,
      warmUpCallId: warmTransferContext.warmUpCallId,
    });

    const originalTask = TasksModule.getTaskByCallId(
      warmTransferContext.originalCallId,
    );
    const warmUpCallTask = TasksModule.getTaskByCallId(
      warmTransferContext.warmUpCallId ?? '',
    );

    if (originalTask) {
      TasksModule.setTaskAttribute(
        originalTask.id,
        'warmTransferContext',
        undefined,
      );
    }
    if (warmUpCallTask) {
      TasksModule.setTaskAttribute(
        warmUpCallTask.id,
        'warmTransferContext',
        undefined,
      );
    }
  }

  /** Returns true if the call is a warm-up call by matching callId or phone number against warm transfer context. */
  private isWarmUpCall(callId: string, remoteIdentity: string): boolean {
    // Check if the call is a warm-up call by matching the call ID and remote identity
    const task = TasksModule.getTaskByCallId(callId);

    // either has warmTransferContext with warmUpCallId
    const hasWarmUpCallId =
      task?.attributes?.warmTransferContext?.warmUpCallId === callId;

    // or has warmTransferContext with targetPhoneNumber that matches the remote identity
    const hasTargetPhoneNumber = task?.attributes?.warmTransferContext
      ? matchesTargetPhone(
          task.attributes.warmTransferContext.targetPhoneNumber,
          remoteIdentity,
        )
      : false;

    return hasWarmUpCallId || hasTargetPhoneNumber;
  }

  /**
   * Resolves the contact center user ID for a transfer target.
   * Checks agentId directly, maps via userById, or matches phone number to a personal line.
   * Returns null for external (non-agent) transfers.
   */
  private getAgentContactCenterId(
    agentId: string | undefined | null,
    transferToNumber: string,
  ): string | null {
    // determine if transfer to agent or number
    const hasAgent = !!agentId;
    const userById = serviceState.userById;
    const userByContactCenterUserId = serviceState.userByContactCenterUserId;

    // already given contactCenterUserId
    if (hasAgent && userByContactCenterUserId[agentId]) {
      return agentId;
    }

    // we need the agent contactcenter user id
    let agentContactCenterId = hasAgent
      ? userById[agentId]?.contactCenterUserId
      : '';

    // cannot find the agent via ID, try to match the external number to a user personal line
    agentContactCenterId ||= Object.values(userById).find((user) =>
      user.personalLine?.phoneNumber?.includes(transferToNumber),
    )?.contactCenterUserId;

    return agentContactCenterId || null;
  }

  /** Returns the current agent's personal line phone number, used for caller ID. */
  private getAgentPhoneNumber() {
    const user = authState.user;

    return user?.personalLine?.phoneNumber;
  }

  /** Returns the current agent's display name, used for caller ID. */
  private getCurrentAgentName(): string | undefined {
    const user = authState.user;

    return user?.name;
  }

  /** Returns an agent's display name by their contact center user ID. */
  private getAgentName(
    agentContactCenterId: string | null,
  ): string | undefined {
    if (!agentContactCenterId) {
      return undefined;
    }

    const userByContactCenterUserId = serviceState.userByContactCenterUserId;

    const agent = userByContactCenterUserId[agentContactCenterId];

    return agent?.name;
  }

  /** Delegates to SoftPhoneModule.callNumberWithCallId with caller ID and custom task attributes. */
  private async makeOutboundViaSoftPhone(
    targetPhoneNumber: string,
    callerPhoneNumber: string,
    callerName: string,
    customTaskAttributes: WarmTransferTaskAttributes,
    displayName?: string,
  ) {
    // note that we don't have to call SoftPhoneModule.prepareOutboundCall here as it's already done within the callNumber method

    return SoftPhoneModule.callNumberWithCallId(
      targetPhoneNumber,
      {
        name: callerName,
        number: callerPhoneNumber,
        tenantId: null,
      },
      displayName,
      null,
      customTaskAttributes,
    );
  }

  /**
   * Polls transfer status every 10s. Uses getWorkflowTransferStatus for queue/queue-originated
   * transfers, getTransferStatus for employee transfers. Retries up to 3 times on error,
   * then marks failed and asks agent to close & classify. On success, deletes the task if
   * answered by a user, otherwise keeps it for agent to close & classify.
   */
  private async handleInProgressTransfer(
    taskId: string,
    errorCount = 0,
  ): Promise<void> {
    const task = TasksModule.getTask(taskId);
    // early return in case of no task or task transferStatus not inProgress
    if (!task || !task.attributes?.transferStatus?.inProgress) {
      return;
    }

    const {
      transferType,
      agentId,
      transferInitiatorAgentId,
      originalTransferType,
    } = task.attributes.transferStatus;

    // Use workflow polling when the transfer is a queue transfer or originated as one (e.g. queue warm transfer → employee)
    const useWorkflowPolling =
      transferType === OutboundCallEntity.Queue ||
      originalTransferType === OutboundCallEntity.Queue;

    let status: CallTransferStatus | null = null;
    let answeredByType: WarmTransferDestinationType | null = null;

    try {
      if (useWorkflowPolling) {
        const result = await getWorkflowTransferStatus(
          task.id,
          transferInitiatorAgentId,
        );
        status = result.status;
        answeredByType = result.answeredByType;
      } else {
        status = await getTransferStatus(task.id, agentId);
      }
    } catch (error) {
      if (errorCount < 3) {
        this.app.logger.warn('transfer:status:poll-retry', {
          taskId,
          errorCount: errorCount + 1,
          error,
        });
        // transient error — retry with incremented error count
        setTimeout(() => {
          this.handleInProgressTransfer(taskId, errorCount + 1);
        }, 10000);
      } else {
        this.app.logger.error('transfer:status:poll-abandoned', {
          taskId,
          errorCount,
          error,
        });
        TasksModule.setTaskAttribute(task.id, 'transferStatus', {
          ...task.attributes.transferStatus,
          inProgress: false,
        });
        ToastModule.showToast({
          title:
            'Unable to check transfer status. Please close & classify the call.',
          status: 'critical',
          duration: 5000,
        });
      }
      return;
    }

    this.app.logger.debug('transfer:status:polling', {
      taskId,
      status,
      transferType,
      errorCount,
    });

    if (
      status === CallTransferStatus.Unknown ||
      status === CallTransferStatus.InProgress
    ) {
      setTimeout(() => {
        this.handleInProgressTransfer(taskId);
      }, 10000);
    } else if (status === CallTransferStatus.Failure) {
      this.app.logger.info('transfer:status:poll-resolved', {
        taskId,
        status: 'failure',
        transferType,
      });
      TasksModule.setTaskAttribute(task.id, 'transferStatus', {
        ...task.attributes.transferStatus,
        inProgress: false,
      });
      ToastModule.showToast({
        title: 'Transfer Completed',
        toastBody:
          'No party answered the transfer. Please close & classify the call and call the customer back.',
        status: 'critical',
        duration: 5000,
      });
    } else if (status === CallTransferStatus.Successful) {
      // Only workflow-polled transfers (queue or queue-originated) have answeredByType.
      // Employee transfers don't reach this with useWorkflowPolling=true,
      // and technician/external transfers don't poll at all — so this is always false for non-queue paths.
      const answeredByNonUser =
        useWorkflowPolling &&
        answeredByType !== WarmTransferDestinationType.User;

      this.app.logger.info('transfer:status:poll-resolved', {
        taskId,
        status: 'successful',
        transferType,
        answeredByType,
        answeredByNonUser,
      });
      TasksModule.setTaskAttribute(task.id, 'transferStatus', {
        ...task.attributes.transferStatus,
        inProgress: false,
      });

      if (answeredByNonUser) {
        // Answered by non-user (e.g. voicemail, IVR, external) — agent must close & classify
        ToastModule.showToast({
          title: 'Transfer Completed',
          toastBody: 'Please close & classify the call.',
          status: 'success',
          duration: 5000,
        });
      } else {
        TasksModule.deleteTask(task.id);
        ToastModule.showToast({
          title: 'Transfer Successful',
          status: 'success',
          duration: 5000,
        });
      }
    }
  }

  /** Resumes polling for any in-progress transfers on app load (requires featureInboxCallWarmTransfers flag). */
  private async handleInProgressTransfersOnLoad(): Promise<void> {
    const featureInboxCallWarmTransfers = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureInboxCallWarmTransfers,
    );
    if (featureInboxCallWarmTransfers) {
      const tasks = TasksModule.getTasks();
      const inProgressTasks = tasks.filter(
        (task) => task.attributes?.transferStatus?.inProgress,
      );

      if (inProgressTasks.length === 0) {
        this.app.logger.debug('transfer:status:none-to-resume-on-load');
      }

      inProgressTasks.forEach((task) => {
        this.app.logger.info('transfer:status:resuming-on-load', {
          taskId: task.id,
          transferType: task.attributes.transferStatus.transferType,
        });
        this.handleInProgressTransfer(task.id);
      });
    }
  }

  /** On phone.call.placed: if this is a warm-up call, records the warmUpCallId in both tasks' context. */
  private handlePhoneCallPlaced(event: CallEvent): void {
    // we need to check if the call that is placed is a warm transfer first leg call
    // if that's the case, we need to update the warmTransferContext with transferringCallId
    const task = TasksModule.getTaskByCallId(event.callId);
    // make sure that this task is a warm up call of the warm transfer
    if (this.isWarmUpCall(event.callId, event.remoteIdentity)) {
      this.app.logger.info('transfer:warm:call-placed', {
        callId: event.callId,
        originalCallId: task?.attributes?.warmTransferContext?.originalCallId,
      });

      const warmTransferContext: WarmTransferContext = {
        ...task?.attributes?.warmTransferContext, // task should be there as it's checked by isWarmUpCall
        warmUpCallId: event.callId,
      };

      this.updateWarmTransferContexts(
        warmTransferContext.originalCallId,
        event.callId,
        warmTransferContext,
      );
    }
  }

  /**
   * On phone.pcs.conversation.fetched: once the PCS conversation SID (channelId)
   * is known for a warm-up call, deactivate its interaction so it isn't counted
   * as a primary one. We wait for this event because the task's id is only
   * updated to the real pcsConversationSid by TasksModule's handler here.
   */
  private handlePcsConversationFetched(
    event: PcsConversationFetchedEvent,
  ): void {
    if (
      FeatureFlagModule.isEnabled(
        FeatureFlagKeys.featureDeactivateWarmTransferInteraction,
      )
    ) {
      const { callId, remoteIdentity } = event.phoneStateUpdateEvent.callState;
      if (!this.isWarmUpCall(callId, remoteIdentity)) {
        return;
      }

      const pcsConversationSid = event.pcsConversation.sid;
      if (!pcsConversationSid) {
        this.app.logger.error('transfer:warm:deactivate:missing-sid', {
          callId,
          event,
        });
        return;
      }
      void this.deactivateWarmUpInteraction(pcsConversationSid, callId);
    }
  }

  /** On phone.call.answered: if this is a warm-up call, sets isWarmUpCallConnected = true on both tasks. */
  private handlePhoneCallAnswered(event: CallEvent): void {
    // we need to check if the call that is answered is a warm transfer first leg call
    // if that's the case, we need to update the warmTransferContext with transferringCallId
    const task = TasksModule.getTaskByCallId(event.callId);
    // make sure that this task is a warm up call of the warm transfer; otherwise we are not interested in this event
    if (this.isWarmUpCall(event.callId, event.remoteIdentity)) {
      this.app.logger.info('transfer:warm:call-answered', {
        callId: event.callId,
        originalCallId: task?.attributes?.warmTransferContext?.originalCallId,
      });

      const warmTransferContext: WarmTransferContext = {
        ...task?.attributes.warmTransferContext,
        isWarmUpCallConnected: true,
      };

      this.updateWarmTransferContexts(
        warmTransferContext.originalCallId,
        event.callId,
        warmTransferContext,
      );
    }
  }

  /** On phone.call.ended/failed/missed: finds any task with matching warm transfer context and cleans it up. */
  private handlePhoneCallEnded(event: CallEvent): void {
    // it's possible that the task is already deleted, so we need to find the remaining warm transfer task
    // by looking at the warmTransferContext

    const tasks = TasksModule.getTasks();

    // we need to find the warm transfer related tasks (original and warm up)
    // we need to clean up the warmTransferContext from any remaining tasks (it's possible that the warm up one got deleted already by Inbox Module)

    const warmTransferTask = tasks.find(
      (t) =>
        t.attributes?.warmTransferContext?.warmUpCallId === event.callId ||
        t.attributes?.warmTransferContext?.originalCallId === event.callId ||
        (t.attributes?.warmTransferContext &&
          matchesTargetPhone(
            t.attributes.warmTransferContext.targetPhoneNumber,
            event.remoteIdentity,
          )),
    );

    if (!warmTransferTask) {
      return; // no task found, nothing to do
    }

    this.app.logger.info('transfer:warm:call-ended', {
      callId: event.callId,
      taskId: warmTransferTask.id,
    });

    // clean up the warm transfer context from the task
    this.deleteWarmTransferContexts(
      warmTransferTask.attributes.warmTransferContext,
    );
  }
}

export default new TransfersModule(
  appManager.events,
  appManager.logger('Transfers'),
);
